import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { uiCard, uiEmpty, uiGet, uiGhostPanel, uiHeader, uiKpis, uiMoney, uiPill, uiRadar, uiRing } from "../ui/kit.js";
import { toast } from "../ui/modals.js";
import { openConversation } from "./conversation.js";
import { lxSlider } from "./launch.js";

/**
 * Team & Talent — the leadership team, redesigned (replaces app.js
 * renderExecutives and ceo.js's delegation panel; loaded after ceo.js).
 *
 *  - Asking for a word: executives with a real reason (a rival's offer,
 *    ambition, pay below fair, a clash with a colleague) ask for a
 *    one-on-one. It's a live conversation (sections/conversation.js, kind
 *    one_on_one): answers move their loyalty and ambition, company morale,
 *    and can give a real raise or pay a real bonus.
 *  - Your leadership team: a card per executive (skill, loyalty, ambition,
 *    flight risk, style, what they run) and empty seats.
 *  - Dossier: tap a card for a radar profile, allies and rivals, the
 *    delegation dial for what they run, a retention-bonus slider, or letting
 *    them go.
 *  - Hiring: choose between three candidates (below / at / above fair pay)
 *    with the skill that pay buys. No typing.
 *  - Charts: the organisation by department and level, and the office-
 *    politics network.
 * Data: /api/people/desk, /api/people/candidates, /api/delegation.
 * Actions: /api/actions/execute (hire_executive, fire_executive,
 * retention_bonus) and /api/delegation.
 */

const PP_LEVELS = ["Junior", "Senior", "Lead", "Manager", "Director"];
const PP_LEVEL_COLOR = { Junior: "#2b6f86", Senior: "#3f97b3", Lead: "#6d5bd0", Manager: "#8b5cf6", Director: "#b8862b" };
const PP_REASON = { offer: "has an offer from a rival", ambition: "wants a bigger role", pay: "thinks their pay is unfair",
                    rival: "is clashing with a colleague" };

function ppInitials(name) { return name.split(" ").map((x) => x[0]).join("").slice(0, 2); }

function ppBar(label, v, tone = "") {
  return `<div class="pp-bar"><span>${esc(label)}</span><div><i class="${tone}" style="width:${Math.max(2, Math.min(100, v))}%"></i></div><b>${Math.round(v)}</b></div>`;
}

function ppCard(e) {
  const risk = e.flight_risk_pct >= 10 ? "bad" : e.flight_risk_pct >= 5 ? "warn" : "good";
  return `
    <button type="button" class="pp-card ${e.wants_a_word ? "asking" : ""}" data-exec="${e.id}">
      ${e.wants_a_word ? `<span class="pp-ask">Wants a word</span>` : ""}
      <div class="pp-card-top"><div class="pp-avatar">${esc(ppInitials(e.name))}</div>
        <div><b>${esc(e.name)}</b><em>${esc(e.short_role)}</em></div></div>
      ${ppBar("Skill", e.skill)}${ppBar("Loyalty", e.loyalty, e.loyalty < 50 ? "bad" : "")}${ppBar("Ambition", e.ambition, "amb")}
      <div class="pp-card-foot">${uiPill(`${e.flight_risk_pct}% flight risk`, risk)} ${uiPill(e.style)}</div>
      ${e.runs.length ? `<div class="pp-runs">Runs: ${e.runs.map((r) => `${esc(r.label)} (${esc(r.mode)})`).join(", ")}</div>` : ""}
    </button>`;
}

function ppOrgChart(depts) {
  if (!depts.length) return uiEmpty("No staff yet", "Departments grow as you spend on them.");
  const max = Math.max(...depts.map((d) => d.headcount), 1);
  return `<div class="pp-org">${depts.map((d) => `
    <div class="pp-org-row"><span>${esc(d.department)}</span>
      <div class="pp-org-bar">${PP_LEVELS.map((lv) => {
        const l = d.levels.find((x) => x.level === lv);
        return l ? `<i style="width:${(l.headcount / max) * 100}%;background:${PP_LEVEL_COLOR[lv]}" title="${esc(lv)}: ${l.headcount} at ${uiMoney(l.avg_salary, { compact: true })}"></i>` : "";
      }).join("")}</div>
      <b>${d.headcount}</b><em>${uiMoney(d.annual_cost, { compact: true })}/yr</em></div>`).join("")}
    <div class="pp-legend">${PP_LEVELS.map((lv) => `<span><i style="background:${PP_LEVEL_COLOR[lv]}"></i>${lv}</span>`).join("")}</div></div>`;
}

function ppNetwork(net) {
  const n = net.nodes.length;
  if (n < 2) return uiEmpty("Not enough of a team yet", "Relationships form as you hire executives.");
  const W = 420, H = 300, cx = W / 2, cy = H / 2, r = 110;
  const pos = Object.fromEntries(net.nodes.map((node, i) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    return [node.name, [cx + Math.cos(a) * r, cy + Math.sin(a) * r]];
  }));
  const edges = net.edges.filter((e) => pos[e.a] && pos[e.b] && e.type !== "Neutral").map((e) => {
    const [x1, y1] = pos[e.a], [x2, y2] = pos[e.b];
    return `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" class="pp-edge ${e.type === "Ally" ? "ally" : "rival"}" data-a="${esc(e.a)}" data-b="${esc(e.b)}"/>`;
  }).join("");
  const nodes = net.nodes.map((node) => {
    const [x, y] = pos[node.name];
    return `<g class="pp-node" data-node="${esc(node.name)}"><circle cx="${x}" cy="${y}" r="20"/>
      <text x="${x}" y="${y + 4}" text-anchor="middle" class="pp-node-in">${esc(ppInitials(node.name))}</text>
      <text x="${x}" y="${y + (y > cy ? 36 : -28)}" text-anchor="middle" class="pp-node-label">${esc(node.short_role)}</text></g>`;
  }).join("");
  return `<svg viewBox="0 0 ${W} ${H}" width="100%" style="max-width:${W}px" class="pp-net" role="img" aria-label="Allies and rivals on the leadership team">${edges}${nodes}</svg>
    <div class="pp-legend"><span><i style="background:var(--sage)"></i>Allies</span><span><i style="background:var(--oxblood)"></i>Rivals</span><span>Neutral pairs not drawn</span></div>`;
}

async function ppAct(type, params) {
  try { toast((await postJSON("/actions/execute", { action: { type, params } })).message); }
  catch (e) { toast(e.message); }
  await window.refreshAll();
}

function ppDossier(e, desk) {
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  const bonusMax = Math.round(e.salary * 0.5 / 1000) * 1000;
  backdrop.innerHTML = `
    <div class="modal modal-page pp-dossier">
      <div class="modal-page-header"><div class="pp-card-top"><div class="pp-avatar big">${esc(ppInitials(e.name))}</div>
        <div><h3>${esc(e.name)}</h3><p>${esc(e.role)} · joined month ${e.hired_month} · ${esc(e.style)} style</p></div></div></div>
      <div class="modal-page-body">
        <div class="pp-dossier-grid">
          <div>${uiRadar([{ axis: "Skill", weight: e.skill }, { axis: "Loyalty", weight: Math.round(e.loyalty) },
                          { axis: "Ambition", weight: Math.round(e.ambition) }, { axis: "Fair pay", weight: Math.min(100, Math.round(e.pay_ratio * 100)) },
                          { axis: "Staying", weight: Math.round(100 - e.flight_risk_pct * 3) }], { caption: "Profile" })}</div>
          <div class="pp-facts">
            <div><span>Salary</span><b>${uiMoney(e.salary)}</b><em>fair for your size: ${uiMoney(e.market_salary)}</em></div>
            <div><span>Flight risk this month</span><b class="${e.flight_risk_pct >= 10 ? "neg" : ""}">${e.flight_risk_pct}%</b><em>from ambition vs loyalty, morale and rival poaching</em></div>
            <div><span>Allies</span><b>${e.allies.length ? e.allies.map(esc).join(", ") : "—"}</b></div>
            <div><span>Rivals</span><b class="${e.rivals.length ? "neg" : ""}">${e.rivals.length ? e.rivals.map(esc).join(", ") : "—"}</b></div>
          </div>
        </div>
        ${e.runs.length ? `<h4 class="pp-h">What they run for you</h4>${e.runs.map((r) => `
          <div class="pp-dial"><span>${esc(r.label)}</span><div class="pp-dial-opts">${["manual", "advise", "autopilot"].map((m) => `
            <button type="button" class="${r.mode === m ? "on" : ""}" data-dial="${esc(r.department)}" data-mode="${m}">${m === "manual" ? "You decide" : m === "advise" ? "They advise" : "They decide"}</button>`).join("")}</div></div>`).join("")}
          <p class="ma-note">On "They decide", ${esc(e.name.split(" ")[0])} makes those calls with their real skill (${e.skill}/100) and ${esc(e.style)} style.</p>` : ""}
        <h4 class="pp-h">Retention bonus</h4>
        ${lxSlider("pp-bonus", "Bonus", Math.round(e.salary * 0.1 / 1000) * 1000, Math.max(1000, bonusMax))}
        <div class="ma-note" id="pp-bonus-note"></div>
        <div class="ma-actions"><button class="primary" id="pp-pay">Pay the bonus</button>
          ${e.had_one_on_one ? "" : `<button id="pp-talk">Have a one-on-one</button>`}
          <button class="danger" id="pp-fire">Let ${esc(e.name.split(" ")[0])} go</button></div>
      </div>
      <div class="modal-page-footer"><span></span><div class="modal-actions"><button id="pp-close">Close</button></div></div>
    </div>`;
  document.body.appendChild(backdrop);
  const $d = (q) => backdrop.querySelector(q);
  const close = () => backdrop.remove();
  $d("#pp-close").addEventListener("click", close);
  const slider = $d("#pp-bonus");
  const note = () => {
    const v = Number(slider.value);
    $d("#pp-bonus-val").textContent = uiMoney(v);
    $d("#pp-bonus-note").textContent = v ? `About ${(v / e.salary * 12).toFixed(1)} months of pay, paid now from cash. Loyalty rises with the amount.` : "";
  };
  slider.addEventListener("input", note); note();
  $d("#pp-pay").addEventListener("click", async () => { close(); await ppAct("retention_bonus", { name: e.name, amount: Number(slider.value) }); });
  $d("#pp-fire").addEventListener("click", async () => {
    if (!window.confirm(`Let ${e.name} go? Their seat will be empty, and the team will notice.`)) return;
    close(); await ppAct("fire_executive", { name: e.name });
  });
  $d("#pp-talk")?.addEventListener("click", () => { close(); openConversation({ kind: "one_on_one", refId: e.one_on_one_ref }); });
  backdrop.querySelectorAll("[data-dial]").forEach((b) => b.addEventListener("click", async () => {
    const r = await postJSON("/delegation", { department: b.dataset.dial, mode: b.dataset.mode });
    toast(r.message);
    backdrop.querySelectorAll(`[data-dial="${b.dataset.dial}"]`).forEach((x) => x.classList.toggle("on", x === b));
  }));
}

async function ppHire(seat) {
  const list = await uiGet(`/people/candidates?role=${encodeURIComponent(seat.role)}`);
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal modal-page pp-hire">
      <div class="modal-page-header"><h3>Hire a ${esc(seat.short_role)}</h3>
        <p>Three candidates are interested. What you pay buys skill: fair pay for a company your size is ${uiMoney(list[0].fair_salary)} a year.</p></div>
      <div class="modal-page-body"><div class="pp-cands">${list.map((c, i) => `
        <div class="pp-cand">
          <div class="pp-card-top"><div class="pp-avatar">${esc(ppInitials(c.name))}</div><div><b>${esc(c.name)}</b><em>${esc(c.profile_label)}</em></div></div>
          <p>${esc(c.pitch)}</p>
          ${ppBar("Skill", c.skill)}
          <div class="pp-cand-facts"><span>Asks</span><b>${uiMoney(c.annual_salary)}/yr</b><span>Style</span><b>${esc(c.style)}</b></div>
          <div class="pp-cand-acts"><button type="button" class="${i === 1 ? "primary" : ""}" data-hire="${i}">Hire ${esc(c.name.split(" ")[0])}</button>
          <button type="button" class="pp-ghost-btn" data-ghost="${i}" title="Forecast the company with this hire">👻 Forecast</button></div>
        </div>`).join("")}</div>
        <div class="sx-forecast" id="pp-ghost"></div></div>
      <div class="modal-page-footer"><span></span><div class="modal-actions"><button id="pp-hclose">Not now</button></div></div>
    </div>`;
  document.body.appendChild(backdrop);
  backdrop.querySelector("#pp-hclose").addEventListener("click", () => backdrop.remove());
  // Ghost Futures before the hire: the recommended candidate first, any other on request
  const ghost = uiGhostPanel(backdrop.querySelector("#pp-ghost"));
  const ghostFor = (i) => {
    const c = list[i];
    backdrop.querySelectorAll("[data-ghost]").forEach((b) => b.classList.toggle("on", +b.dataset.ghost === i));
    ghost.run({ type: "hire_executive", params: { role: c.role, name: c.name, annual_salary: c.annual_salary } });
  };
  backdrop.querySelectorAll("[data-ghost]").forEach((b) => b.addEventListener("click", () => ghostFor(+b.dataset.ghost)));
  ghostFor(Math.min(1, list.length - 1));
  backdrop.querySelectorAll("[data-hire]").forEach((b) => b.addEventListener("click", async () => {
    const c = list[Number(b.dataset.hire)];
    backdrop.remove();
    await ppAct("hire_executive", { role: c.role, name: c.name, annual_salary: c.annual_salary });
  }));
}

window.renderExecutives = async function (main) {
  const desk = await uiGet("/people/desk");
  const asking = desk.executives.filter((e) => e.wants_a_word);
  const atRisk = desk.executives.filter((e) => e.flight_risk_pct >= 10).length;
  main.innerHTML = `
    <div class="sx pp">
      ${uiHeader("Team & Talent", "Your leadership team runs the company with you. Keep the good ones, listen when they ask for a word, and hire well.")}
      <div class="pp-top">
        <div class="pp-morale">${uiRing(desk.morale, "morale", 92)}<div><b>Company morale</b><span>${desk.morale >= 70 ? "Healthy" : desk.morale >= 50 ? "Holding" : "Low: people are leaving"}</span></div></div>
        ${uiKpis([
          { label: "People", value: desk.headcount.toLocaleString("en-US") },
          { label: "Payroll", value: `${uiMoney(desk.payroll, { compact: true })}/yr`, sub: `wages up ${desk.salary_inflation_pct}% since founding` },
          { label: "Rivals poaching", value: `${desk.poaching_pressure_pct}%`, sub: "pressure on your executives" },
          { label: "Executives at risk", value: atRisk, tone: atRisk ? "neg" : "" },
          { label: "Team cohesion", value: desk.cohesion === null || desk.cohesion === undefined ? "—" : Math.round(desk.cohesion), sub: "feeds the Board's confidence" },
        ])}
      </div>
      ${asking.length ? `<section class="pp-asking">${asking.map((e) => `
        <div class="pp-ask-card"><div class="pp-avatar">${esc(ppInitials(e.name))}</div>
          <div><b>${esc(e.name)}</b> (${esc(e.short_role)}) ${esc(PP_REASON[e.wants_a_word])} and asked for a word.</div>
          <button type="button" class="primary" data-talk="${e.one_on_one_ref}">Meet now</button></div>`).join("")}</section>` : ""}
      ${uiCard("Your leadership team", `<div class="pp-grid">${desk.executives.map(ppCard).join("")}
        ${desk.empty_seats.map((s) => `<button type="button" class="pp-card empty" data-seat="${esc(s.role)}">
          <div class="pp-avatar empty">+</div><b>No ${esc(s.short_role)}</b><em>Fair pay for your size: ${uiMoney(s.market_salary, { compact: true })}/yr</em>
          <span class="pp-hire-cta">Meet candidates →</span></button>`).join("")}</div>`,
        { sub: "Tap someone for their profile" })}
      ${desk.benchmark ? uiCard("Leadership benchmark", `<div class="pp-bench">${desk.benchmark.rows.map((r) => `
        <div class="pp-bench-row ${esc(r.tone)}"><span>${esc(r.label)}</span><b>${esc(r.value)}</b><em>${esc(r.why)}</em></div>`).join("")}</div>`,
        { sub: "Your leadership against real companies of your size. " + desk.benchmark.source }) : ""}
      <div class="sx-grid-2">
        ${uiCard("The organisation", ppOrgChart(desk.departments) + (desk.pipeline.length ? `<div class="pp-pipe">${desk.pipeline.map((p) =>
          `<span class="pp-chip">${p.headcount} ${esc(p.level)} ${esc(p.department)} · ${p.months_until_join === 0 ? "join this month" : `in ${p.months_until_join} mo`}</span>`).join("")}</div>` : ""),
          { sub: "People by department and level; hires in the pipeline join after a real lead time" })}
        ${uiCard("Office politics", ppNetwork(desk.network), { sub: "Allies help each other; rivals drag the team (and the Board's view of it) down" })}
      </div>
    </div>`;
  main.querySelectorAll("[data-exec]").forEach((b) => b.addEventListener("click", () => ppDossier(desk.executives.find((e) => e.id === Number(b.dataset.exec)), desk)));
  main.querySelectorAll("[data-seat]").forEach((b) => b.addEventListener("click", () => ppHire(desk.empty_seats.find((s) => s.role === b.dataset.seat))));
  main.querySelectorAll("[data-talk]").forEach((b) => b.addEventListener("click", () => openConversation({ kind: "one_on_one", refId: Number(b.dataset.talk) })));
  main.querySelectorAll(".pp-node").forEach((g) => {
    g.addEventListener("mouseenter", () => main.querySelectorAll(".pp-edge").forEach((l) => l.classList.toggle("dim", l.dataset.a !== g.dataset.node && l.dataset.b !== g.dataset.node)));
    g.addEventListener("mouseleave", () => main.querySelectorAll(".pp-edge").forEach((l) => l.classList.remove("dim")));
  });
};
